import { router, usePage } from '@inertiajs/vue3';
import posthog from 'posthog-js';
import { computed } from 'vue';
import config from '@/config';

let initialized = false;

interface PostHogUser {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    global_id?: string;
    role?: { name: string };
}

interface TenantData {
    id: string;
    organisation_name: string;
    tenant_type: string;
    is_supplier: boolean;
    is_agent: boolean;
}

interface InitPostHogOptions {
    /** Defaults to the POSTHOG_SESSION_RECORDING config. Off for anonymous marketing visitors. */
    sessionRecording?: boolean;
    /** Defaults to on. Off on the marketing pages, where the surveys bundle is ~34 KB of unused JavaScript. */
    surveys?: boolean;
}

const initPostHog = ({ sessionRecording = config.POSTHOG_SESSION_RECORDING, surveys = true }: InitPostHogOptions = {}) => {
    if (initialized || !config.POSTHOG_ENABLED || !config.POSTHOG_API_KEY || typeof window === 'undefined') {
        return;
    }

    posthog.init(config.POSTHOG_API_KEY, {
        api_host: config.POSTHOG_HOST,
        person_profiles: 'identified_only',
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: config.POSTHOG_AUTOCAPTURE,
        disable_session_recording: !sessionRecording,
        disable_surveys: !surveys,

        session_recording: {
            maskAllInputs: false,
            maskInputFn: (text: string, element?: HTMLElement) => {
                const el = element as HTMLInputElement | undefined;

                if (el?.type === 'password') {
                    return '*'.repeat(text.length);
                }

                if (el?.dataset?.phMask !== undefined) {
                    return '*'.repeat(text.length);
                }

                return text;
            },
        },

        loaded: (ph) => {
            if (config.APP_ENVIRONMENT === 'local') {
                ph.debug();
            }
        },
    });

    initialized = true;
};

const identifyUser = (user: PostHogUser, tenant?: TenantData | null) => {
    if (!initialized) {
        return;
    }

    const properties: Record<string, unknown> = {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        name: `${user.first_name} ${user.last_name}`.trim(),
    };

    if (user.role?.name) {
        properties.role = user.role.name;
    }

    if (user.global_id) {
        properties.global_id = user.global_id;
    }

    if (tenant) {
        properties.tenant_type = tenant.tenant_type;
        properties.is_supplier = tenant.is_supplier;
        properties.is_agent = tenant.is_agent;
    }

    // Key the person on global_id so frontend and backend events (which use
    // causer global_id as distinct id) merge into one PostHog person.
    // Tenant-local numeric ids are only a fallback — they can collide across
    // tenant schemas.
    posthog.identify(user.global_id ?? String(user.id), properties);

    if (tenant) {
        posthog.group('tenant', tenant.id, {
            name: tenant.organisation_name,
            tenant_type: tenant.tenant_type,
            is_supplier: tenant.is_supplier,
            is_agent: tenant.is_agent,
        });
    }
};

const resetUser = () => {
    if (!initialized) {
        return;
    }

    posthog.reset();
};

export const usePostHog = () => {
    const page = usePage<{ auth?: { user?: PostHogUser; tenant?: TenantData } }>();
    const tenant = computed(() => page.props.auth?.tenant ?? null);

    const capture = (event: string, properties?: Record<string, unknown>) => {
        if (!initialized) {
            return;
        }

        posthog.capture(event, {
            ...properties,
            source: 'frontend',
        });
    };

    const captureWithTenant = (event: string, properties?: Record<string, unknown>) => {
        if (!initialized) {
            return;
        }

        posthog.capture(event, {
            ...properties,
            source: 'frontend',
            $groups: tenant.value ? { tenant: tenant.value.id } : undefined,
        });
    };

    const setPersonProperty = (key: string, value: unknown) => {
        if (!initialized) {
            return;
        }

        posthog.people.set({ [key]: value });
    };

    const trackPageView = (pageName?: string, properties?: Record<string, unknown>) => {
        if (!initialized) {
            return;
        }

        posthog.capture('$pageview', {
            ...properties,
            page_name: pageName,
        });
    };

    const trackFormSubmission = (formName: string, properties?: Record<string, unknown>) => {
        capture('form_submitted', {
            form_name: formName,
            ...properties,
        });
    };

    const trackFeatureUsed = (feature: string, properties?: Record<string, unknown>) => {
        captureWithTenant('feature_used', {
            feature,
            ...properties,
        });
    };

    const trackError = (errorType: string, errorMessage: string, properties?: Record<string, unknown>) => {
        capture('error_occurred', {
            error_type: errorType,
            error_message: errorMessage,
            ...properties,
        });
    };

    const isFeatureEnabled = (flagKey: string): boolean => {
        if (!initialized) {
            return false;
        }

        return posthog.isFeatureEnabled(flagKey) ?? false;
    };

    const getFeatureFlag = (flagKey: string): string | boolean | undefined => {
        if (!initialized) {
            return undefined;
        }

        return posthog.getFeatureFlag(flagKey);
    };

    return {
        capture,
        captureWithTenant,
        setPersonProperty,
        trackPageView,
        trackFormSubmission,
        trackFeatureUsed,
        trackError,
        isFeatureEnabled,
        getFeatureFlag,
        identifyUser,
        resetUser,
        posthog: initialized ? posthog : null,
    };
};

let listenerRegistered = false;

const registerNavigationListener = () => {
    if (listenerRegistered) {
        return;
    }

    listenerRegistered = true;

    router.on('navigate', (event) => {
        const page = event.detail.page;
        const auth = (page.props as Record<string, unknown>)?.auth as { user?: PostHogUser; tenant?: TenantData } | undefined;

        if (auth?.user) {
            identifyUser(auth.user, auth.tenant);
        }
    });
};

export { identifyUser, initPostHog, registerNavigationListener, resetUser };
